import { Component, HostListener } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Platform, NavController } from '@ionic/angular';
import { UserService } from 'src/app/services/user/user.service';
import { UserType } from './enums/user';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { StatusBar } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { ApiHelperService } from './services/api-helper-service/api-helper.service';
import { GlobalService } from './services/global/global.service';
import { App } from '@capacitor/app';
import { JailbreakRoot }  from '@basecom-gmbh/capacitor-jailbreak-root-detection';
import { DeviceSecurityDetect } from '@capacitor-community/device-security-detect';
import { ModalController } from '@ionic/angular';
import { ClientSupportComponent } from './modal/client-support/client-support-modal.component';
import { PlayIntegrity } from '@capacitor-community/play-integrity';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { Device } from '@capacitor/device';

declare var IRoot: any;
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent {
  appPages = [];
  showMenu: boolean = true;
  showTabs: boolean = true;
  isLargeScreen = false;

  langs = [
    { code: 'en', name: 'English'},
    { code: 'ms', name: 'Bahasa Melayu'}
  ];

  constructor(
    private platform: Platform,
    private translate: TranslateService,
    private userService: UserService,
    private navCtrl: NavController,
    private router: Router,
    private apiHelperService: ApiHelperService,
    private globalService:GlobalService,
    private modalCtrl: ModalController
  ) {
    this.translate.setDefaultLang('ms');
    this.initializeLanguage();
    if (environment.production) {
      console.warn(`🚨 Console output is disabled on production!`);
      console.log = () => {};
      console.warn = () => {};
      console.info = () => {};
      console.debug = () => {};
    }
    this.initializeApp();
    this.checkRootStatus();
    this.checkScreenSize();
    this.checkRoot();
  }

  async initializeApp() {
    await this.platform.ready();

    if (Capacitor.getPlatform() === 'ios') {
      await StatusBar.setOverlaysWebView({ overlay: false });
    }

    await ScreenOrientation.lock({ orientation: 'portrait' });
  }

  // Language initialization
  initializeLanguage() {
    let language = localStorage.getItem('Language');
    if (language) {
      this.setLanguage(language);
    } else {
      // Default language (first one in the list)
      this.setLanguage(this.langs[0].code);
    }
  }

  setLanguage(langCode: string) {
    const selectedLang = this.langs.find(lang => lang.code === langCode);
    if (selectedLang) {
      localStorage.setItem('Language', langCode);
      new Promise<void>((resolve, reject) => {
        this.translate.use(langCode).subscribe(() => resolve(), err => reject(err));
      })
        .then(() => this.loadTranslationOverrides(langCode))
        .catch(error => console.log('setLanguage: Error: ', error));
    }
  }

  async loadTranslationOverrides(langCode: string) {
    const cacheKey = `translation-${langCode}`;
    const cached = localStorage.getItem(cacheKey);

    // check is there a cached translation. if yes then directly load the cached translation, else does nothing and continue to call API.
    if (cached) {
      try {
        this.translate.setTranslation(langCode, JSON.parse(cached), true);
        return;
      } catch (cacheError) {
        console.log('loadTranslationOverrides: cache parse error, fetching fresh: ', cacheError);
      }
    }

    // in case the API calls fail still can use backup(en.json & ms.json) to serve for translation.
    const backup = JSON.parse(JSON.stringify(this.translate.translations[langCode] || {}));

    try {
      const searchFilter = {
        languageCode: langCode,
        referenceNumber: this.globalService.generateUUID(),
        username: this.globalService.username,
        channelId: this.globalService.channelId
      };
      const response: any = await this.apiHelperService.getAppSettings(searchFilter);
      const translations = response?.data?.I18nTranslation ?? [];
      const overrides = translations.reduce((acc: any, row: any) => {
        const segments = String(row.translationKey).split('.');
        let node = acc;
        for (let i = 0; i < segments.length - 1; i++) {
          if (typeof node[segments[i]] !== 'object' || node[segments[i]] === null) {
            node[segments[i]] = {};
          }
          node = node[segments[i]];
        }
        node[segments[segments.length - 1]] = row.translationValue;
        return acc;
      }, {});
      if (Object.keys(overrides).length) {
        this.translate.setTranslation(langCode, overrides, true);
        localStorage.setItem(cacheKey, JSON.stringify(overrides));
      }
    } catch (error) {
      this.translate.setTranslation(langCode, backup, false);
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          this.translate.setTranslation(langCode, JSON.parse(cached), true);
        }
      } catch (cacheError) {
        console.log('loadTranslationOverrides: cache restore error: ', cacheError);
      }
    }
  }

  generateNonce(): string {
    const array = new Uint8Array(32); // 256 bits random
    window.crypto.getRandomValues(array);

    let base64 = btoa(String.fromCharCode(...array));
    base64 = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    return base64;
  }

  async checkRoot() {
    if (Capacitor.getPlatform() === 'android') {
      try {
        await this.platform.ready();

        const deviceId = await Device.getId();
        const nonce = this.generateNonce();

        const result = await PlayIntegrity.requestIntegrityToken({
          nonce: nonce,
          googleCloudProjectNumber: environment.googleCloudProjectNumber
        });

        const res: any = await this.apiHelperService.verifyDevice(
          result.token,
          deviceId.identifier
        );

        if (!res.success) {
          alert(`Security Alert: ${res.reason}`);
          setTimeout(() => {
            App.exitApp();
          }, 1000);
          return;
        }
      } catch (err) {
        console.error("Play Integrity failed (stringified):", JSON.stringify(err, null, 2));

        alert("Security Alert: Integrity check failed. Exiting app.");
        setTimeout(() => {
          App.exitApp();
        }, 1000);
      }
    }
  }

async checkRootStatus() {
  if (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android') {
    try {
      // First check
      const valueJailbreakRoot = await JailbreakRoot.isJailbrokenOrRooted();
      let rootedByFirstCheck = valueJailbreakRoot.result;
      //second check
      const rootedBySecondCheck = await new Promise<boolean>((resolve) => {
      IRoot.isRooted(resolve, () => {
        console.log("IRoot check failed, assuming not rooted.");
        resolve(false);
      });
    });
      //third check
     const rootedByThirdCheck = (await DeviceSecurityDetect.isJailBreakOrRooted()).value;

     if (rootedByFirstCheck || rootedBySecondCheck || rootedByThirdCheck) {
      alert('Security Alert: This app cannot run on a jailbroken or rooted device.');
      setTimeout(() => App.exitApp(), 1000);
    } else {
      console.log('The device is secure.');
    }
    } catch (error) {
      console.error('Error checking jailbreak/root status:', error);
    }
  } else {
    console.log('Platform is not iOS or Android — skipping root check.');
  }
}

  getAvailableLanguages() {
    return this.langs;
  }

  getCurrentLanguage() {
    const currentLangCode = this.translate.currentLang || this.langs[0].code;
    return currentLangCode;
  }

  // Update the menu based on user type
  async updateMenuList() {
  const userStorage: any = await this.userService.getUserStorage();
  const userId: number = Number(userStorage.userId);
  console.log("userid", userId);

  const searchFilter = {
    filter: JSON.stringify({
      where: { id: userId }
    }),
    referenceNumber: this.globalService.generateUUID(),
    username: this.globalService.username,
    channelId: this.globalService.channelId
  };

  const response: any = await this.apiHelperService.retrieveClientListing(searchFilter);
  const userInfo: any = response.data.user.find((u: any) => Number(u.id) === userId);
  console.log('userInfo', userInfo);
  const passwordResetRequired = userInfo?.passwordResetRequired;
  const notRequiredChangePassword = passwordResetRequired === false || passwordResetRequired == null;
  const showProfile = this.userService.userType === UserType.CUSTOMER && notRequiredChangePassword;

  // this.appPages = [
  //   { title: 'menuTabs.home', url: '/home', icon: 'home', show: notRequiredChangePassword },
  //   { title: 'menuTabs.applicationListing', url: '/application-listing', icon: 'list', show: this.canShowApplicationListing() },
  //   { title: 'menuTabs.onboardingDraftListing', url: '/onboarding-draft-listing', icon: 'bookmark', show: this.canShowDraftListing() },
  //   { title: 'menuTabs.profile', url: '/user-profile', icon: 'person', show: showProfile },
  //   { title: 'menuTabs.setting', url: '/change-password', icon: 'settings', show: true },
  //   { title: 'menuTabs.backOffice', url: environment.backOfficeUrl, icon: 'business', showBO: this.canShowBackOffice() },
  //   { title: 'button.loanCalculator', url: '/loan-calculator', icon: 'assets/images/icon-calculator.png', show: true },
  //   { title: 'button.supportFeature', url: '/client-support', icon: 'assets/icon/support-feature.svg', show: this.canShowClientSupport() }
  // ];

  this.appPages = [
    { title: 'menuTabs.home', url: '/home-new', icon: 'assets/images/newDanaPlus/iconDanaPlusLogo.png', show: notRequiredChangePassword },
    { title: 'menuTabs.applicationListing', url: '/application-listing', icon: 'list', show: this.canShowApplicationListing() },
    { title: 'menuTabs.onboardingDraftListing', url: '/onboarding-draft-listing', icon: 'bookmark', show: this.canShowDraftListing() },
    { title: 'menuTabs.profile', url: '/user-profile', icon: 'assets/images/newDanaPlus/iconProfile.png', show: showProfile },
    { title: 'menuTabs.setting', url: '/change-password', icon: 'assets/images/newDanaPlus/iconSetting.png', show: true },
    { title: 'menuTabs.backOffice', url: environment.backOfficeUrl, icon: 'business', showBO: this.canShowBackOffice() },
    { title: 'button.loanCalculator', url: '/loan-calculator', icon: 'assets/images/icon-calculator.png', show: true },
    { title: 'button.supportFeature', url: '/client-support', icon: 'assets/images/newDanaPlus/iconCustService.png', show: this.canShowClientSupport() }
  ];

  console.log(this.appPages);
}



  // Checks based on user type
  canShowBackOffice() {
    return this.userService.userType === UserType.AGENT;
  }

  canShowApplicationListing() {
    return this.userService.userType === UserType.AGENT;
  }

  canShowDraftListing() {
    return this.userService.userType === UserType.AGENT;
  }

  canShowClientSupport() {
    return this.userService.userType === UserType.CUSTOMER;
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkScreenSize();
  }

  checkScreenSize() {
    this.isLargeScreen = window.innerWidth >= 1000;
  }

  async onMenuClick(event: Event, page: any) {
    if (page.url === '/client-support') {
      event.preventDefault();
      event.stopPropagation();

      const modal = await this.modalCtrl.create({
        component: ClientSupportComponent,
        cssClass: 'client-support-modal' // start with 'home'
      });
      await modal.present();
      return; // prevent normal routing
    }
  }
}
